import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiDownload,
  FiEdit2,
  FiLoader,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const displayName = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const rowData = (row) => row.normalizedData || row.rawData || {};

export default function ImportJobReviewPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [job, setJob] = useState(null);
  const [referenceLookups, setReferenceLookups] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [temporaryCredentials, setTemporaryCredentials] = useState([]);

  const load = useCallback(async () => {
    if (!accessToken || !jobId) return;
    setLoading(true);
    setError("");
    try {
      const nextJob = await apiRequest(`/imports/jobs/${jobId}`, {
        accessToken,
      });
      setJob(nextJob);

      const lookups = await apiRequest(`/imports/lookups/${nextJob.type}`, {
        accessToken,
      });
      setReferenceLookups(
        Object.fromEntries(
          (lookups || []).map((lookup) => [lookup.field, lookup]),
        ),
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to open this import job.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const headers = Array.isArray(job?.headers) ? job.headers : [];
  const activeRows = useMemo(
    () => (job?.rows || []).filter((row) => row.status !== "DELETED"),
    [job],
  );
  const visibleRows = useMemo(() => {
    if (filter === "NEEDS_ATTENTION") {
      return activeRows.filter((row) =>
        ["ERROR", "WARNING"].includes(row.status),
      );
    }
    if (filter === "READY")
      return activeRows.filter((row) => row.status === "READY");
    return activeRows;
  }, [activeRows, filter]);

  const canEdit = [
    "VALIDATING",
    "READY_TO_CONFIRM",
    "COMPLETED_WITH_ERRORS",
  ].includes(job?.status);

  const request = async (path, options = {}) => {
    setSaving(true);
    setError("");
    try {
      const updated = await apiRequest(path, { accessToken, ...options });
      if (Array.isArray(updated?.temporaryCredentials)) {
        setTemporaryCredentials(updated.temporaryCredentials);
      }
      setJob(updated);
      return updated;
    } catch (requestError) {
      setError(requestError.message || "Import action failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveRow = async () => {
    if (!editingRow || !job) return;
    const updated = await request(
      `/imports/jobs/${job.id}/rows/${editingRow.rowNumber}`,
      { method: "PATCH", body: { data: editingRow.data } },
    );
    if (updated) setEditingRow(null);
  };

  const deleteRow = async (row) => {
    if (!job) return;
    if (
      !window.confirm(
        `Remove spreadsheet row ${row.rowNumber} from this import?`,
      )
    ) {
      return;
    }
    const updated = await request(
      `/imports/jobs/${job.id}/rows/${row.rowNumber}`,
      {
        method: "DELETE",
      },
    );
    if (updated && editingRow?.rowNumber === row.rowNumber) setEditingRow(null);
  };

  const revalidate = () =>
    request(`/imports/jobs/${job.id}/revalidate`, { method: "POST" });

  const confirmImport = async () => {
    if (!job) return;
    const reviewedRows =
      Number(job.readyRows || 0) + Number(job.warningRows || 0);
    if (
      !window.confirm(
        `Import ${reviewedRows} reviewed row(s) into live ${displayName(job.type)} data? This cannot be undone automatically.`,
      )
    ) {
      return;
    }
    await request(`/imports/jobs/${job.id}/confirm`, { method: "POST" });
  };

  const downloadTemporaryCredentials = () => {
    const quote = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const records = [
      [
        "Employee code",
        "Employee name",
        "Email",
        "Phone",
        "Temporary password",
      ],
      ...temporaryCredentials.map((item) => [
        item.employeeCode,
        item.fullName,
        item.email,
        item.phone,
        item.temporaryPassword,
      ]),
    ];
    const blob = new Blob(
      [records.map((record) => record.map(quote).join(",")).join("\n")],
      {
        type: "text/csv;charset=utf-8",
      },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `charms-employee-temporary-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <FiLoader size={30} className="animate-spin text-primary-800" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <FiAlertCircle className="mx-auto text-red-600" size={34} />
        <h1 className="mt-4 text-2xl font-bold text-slate-950">
          Import job unavailable
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {error || "This import may have been removed."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/admindashboard/tools/importcenter")}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-bold text-white"
        >
          <FiArrowLeft /> Back to Import Centre
        </button>
      </div>
    );
  }

  return (
    <div className="-mx-4 -my-6 min-h-[calc(100vh-64px)] bg-slate-50 sm:-mx-6 lg:-mx-8">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <button
              type="button"
              onClick={() => navigate("/admindashboard/tools/importcenter")}
              className="mt-0.5 rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
              aria-label="Back to Import Centre"
            >
              <FiArrowLeft />
            </button>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-800">
                {displayName(job.type)} import review
              </p>
              <h1 className="mt-1 truncate text-xl font-bold text-slate-950 sm:text-2xl">
                {job.originalFileName}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Review every row before it reaches live CHARMS data.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={job.status} />
            <button
              type="button"
              disabled={!canEdit || saving}
              onClick={revalidate}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <FiRefreshCw className={saving ? "animate-spin" : ""} />{" "}
              Revalidate
            </button>
            <button
              type="button"
              disabled={!canEdit || Number(job.errorRows || 0) > 0 || saving}
              onClick={confirmImport}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiCheckCircle /> Confirm import
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-5 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            <span>{error}</span>
            <button
              type="button"
              className="ml-auto"
              onClick={() => setError("")}
              aria-label="Dismiss error"
            >
              <FiX />
            </button>
          </div>
        ) : null}

        {temporaryCredentials.length ? (
          <section className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-bold">
                  Save the new employee credentials now
                </p>
                <p className="mt-1 leading-5 text-amber-800">
                  These temporary passwords are shown only in this page session
                  and cannot be recovered from import history.
                </p>
              </div>
              <button
                type="button"
                onClick={downloadTemporaryCredentials}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-amber-900 px-3 py-2.5 text-xs font-bold text-white hover:bg-amber-950"
              >
                <FiDownload /> Download CSV
              </button>
            </div>
          </section>
        ) : null}

        <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <ReviewStat label="Ready" value={job.readyRows} tone="green" />
          <ReviewStat label="Warnings" value={job.warningRows} tone="amber" />
          <ReviewStat label="Errors to fix" value={job.errorRows} tone="red" />
          <ReviewStat
            label="Rows removed"
            value={job.skippedRows}
            tone="slate"
          />
        </section>

        <div className="relative">
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div>
                <h2 className="font-bold text-slate-950">Staged rows</h2>
                <p className="mt-1 text-sm text-slate-500">
                  The full spreadsheet stays visible here. Select a row to open
                  its editor.
                </p>
              </div>
              <div className="flex rounded-xl bg-slate-100 p-1 text-xs font-bold text-slate-600">
                {[
                  ["ALL", `All (${activeRows.length})`],
                  [
                    "NEEDS_ATTENTION",
                    `Needs review (${Number(job.errorRows || 0) + Number(job.warningRows || 0)})`,
                  ],
                  ["READY", `Ready (${job.readyRows || 0})`],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilter(value)}
                    className={`rounded-lg px-3 py-2 transition ${filter === value ? "bg-white text-primary-800 shadow-sm" : "hover:text-slate-950"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[calc(100vh-325px)] min-h-[520px] overflow-auto">
              <table className="w-full min-w-[1050px] text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Row</th>
                    <th className="px-4 py-3">Status</th>
                    {headers.map((header) => (
                      <th key={header} className="px-4 py-3">
                        {referenceLookups[header]?.label || header}
                      </th>
                    ))}
                    <th className="px-4 py-3">Validation</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const data = rowData(row);
                    const selected = editingRow?.rowNumber === row.rowNumber;
                    return (
                      <tr
                        key={row.id}
                        onClick={() =>
                          setEditingRow({
                            rowNumber: row.rowNumber,
                            data: { ...data },
                          })
                        }
                        className={`cursor-pointer border-t border-slate-100 align-top transition ${selected ? "bg-primary-50/70" : "hover:bg-slate-50"}`}
                      >
                        <td className="px-4 py-3 font-semibold text-slate-700">
                          {row.rowNumber}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={row.status} />
                        </td>
                        {headers.map((header) => (
                          <td
                            key={header}
                            className="max-w-48 truncate px-4 py-3 text-slate-700"
                            title={String(data[header] ?? "")}
                          >
                            {String(data[header] ?? "—")}
                          </td>
                        ))}
                        <td className="max-w-64 px-4 py-3">
                          {Array.isArray(row.validation) &&
                          row.validation.length ? (
                            <p className="line-clamp-2 text-xs leading-5 text-red-700">
                              {row.validation
                                .map(
                                  (issue) =>
                                    `${issue.field ? `${issue.field}: ` : ""}${issue.message}`,
                                )
                                .join(" · ")}
                            </p>
                          ) : (
                            <span className="text-xs font-semibold text-green-700">
                              Ready
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEditingRow({
                                rowNumber: row.rowNumber,
                                data: { ...data },
                              });
                            }}
                            className="rounded-lg bg-primary-50 p-2 text-primary-800 hover:bg-primary-100"
                            aria-label={`Review row ${row.rowNumber}`}
                          >
                            <FiEdit2 />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!visibleRows.length ? (
                    <tr>
                      <td
                        colSpan={headers.length + 4}
                        className="px-5 py-12 text-center text-sm text-slate-500"
                      >
                        No rows match this view.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          {editingRow ? (
            <div className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[1px]">
              <aside className="ml-auto flex h-full w-full max-w-[460px] flex-col border-l border-slate-200 bg-white shadow-2xl">
                <RowReviewPanel
                  headers={headers}
                  row={editingRow}
                  referenceLookups={referenceLookups}
                  saving={saving}
                  canEdit={canEdit}
                  onChange={setEditingRow}
                  onSave={saveRow}
                  onDelete={() => {
                    const existing = activeRows.find(
                      (item) => item.rowNumber === editingRow.rowNumber,
                    );
                    if (existing) deleteRow(existing);
                  }}
                  onClose={() => setEditingRow(null)}
                />
              </aside>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

function RowReviewPanel({
  headers,
  row,
  referenceLookups,
  saving,
  canEdit,
  onChange,
  onSave,
  onDelete,
  onClose,
}) {
  return (
    <div>
      <div className="flex items-start justify-between border-b border-slate-100 p-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-800">
            Row review
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-950">
            Spreadsheet row {row.rowNumber}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
        >
          <FiX />
        </button>
      </div>
      <div className="max-h-[calc(100vh-265px)] space-y-4 overflow-y-auto p-5">
        {headers.map((header) => {
          const lookup = referenceLookups[header];
          const datalistId = `import-lookup-${row.rowNumber}-${header}`;
          return (
            <label
              key={header}
              className="block text-sm font-semibold text-slate-700"
            >
              {lookup?.label || displayName(header)}
              <input
                disabled={!canEdit || saving}
                list={lookup ? datalistId : undefined}
                value={row.data[header] ?? ""}
                onChange={(event) =>
                  onChange({
                    ...row,
                    data: { ...row.data, [header]: event.target.value },
                  })
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal text-slate-900 outline-none transition focus:border-primary-700 focus:ring-2 focus:ring-primary-100 disabled:bg-slate-50"
              />
              {lookup ? (
                <>
                  <datalist id={datalistId}>
                    {lookup.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </datalist>
                  <span className="mt-1 block text-[11px] font-normal text-slate-500">
                    Search, select, or type a {lookup.label.toLowerCase()} code.
                  </span>
                </>
              ) : null}
            </label>
          );
        })}
      </div>
      {canEdit ? (
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 p-5">
          <button
            type="button"
            disabled={saving}
            onClick={onDelete}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <FiTrash2 /> Remove
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-3 py-2.5 text-sm font-bold text-white hover:bg-primary-900 disabled:opacity-50"
          >
            <FiSave /> Save row
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }) {
  const tones = {
    READY: "bg-green-100 text-green-700",
    WARNING: "bg-amber-100 text-amber-800",
    ERROR: "bg-red-100 text-red-700",
    IMPORTED: "bg-blue-100 text-blue-700",
    DELETED: "bg-slate-100 text-slate-600",
    COMPLETED: "bg-green-100 text-green-700",
    READY_TO_CONFIRM: "bg-amber-100 text-amber-800",
    VALIDATING: "bg-amber-100 text-amber-800",
    IMPORTING: "bg-blue-100 text-blue-700",
    COMPLETED_WITH_ERRORS: "bg-red-100 text-red-700",
    FAILED: "bg-red-100 text-red-700",
    CANCELLED: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${tones[status] || "bg-slate-100 text-slate-600"}`}
    >
      {displayName(status)}
    </span>
  );
}

function ReviewStat({ label, value, tone }) {
  const tones = {
    green: "border-green-100 bg-green-50 text-green-800",
    amber: "border-amber-100 bg-amber-50 text-amber-800",
    red: "border-red-100 bg-red-50 text-red-800",
    slate: "border-slate-200 bg-white text-slate-800",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-70">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold">{value || 0}</p>
    </div>
  );
}
